/**
 * Force update store for managing frontend version state.
 *
 * This store tracks:
 * - The initial timestamp fetched when the app loaded
 * - Whether a force update modal should be shown
 * - The comparison logic between initial and current timestamps
 */
import { create } from "zustand";

export type ForceUpdateState = {
  /** The timestamp fetched when the app first loaded. */
  initialTimestamp: number | null;

  /** Whether the force update modal is currently shown. */
  showModal: boolean;

  /** Whether we've already initialized (prevents re-fetching on HMR). */
  initialized: boolean;

  /** Set the initial timestamp (called once on app load). */
  setInitialTimestamp: (timestamp: number | null) => void;

  /** Check if we need to show the update modal. */
  checkForUpdate: (currentTimestamp: number | null) => void;

  /** Show the modal (called when mismatch detected). */
  showUpdateModal: () => void;

  /** Hide the modal (not typically used, but for completeness). */
  hideUpdateModal: () => void;
};

export const useForceUpdateStore = create<ForceUpdateState>()((set, get) => ({
  initialTimestamp: null,
  showModal: false,
  initialized: false,

  setInitialTimestamp: (timestamp) => {
    set({ initialTimestamp: timestamp, initialized: true });
  },

  checkForUpdate: (currentTimestamp) => {
    const { initialTimestamp, showModal, initialized } = get();

    console.debug("[ForceUpdate] checkForUpdate called", {
      currentTimestamp,
      initialTimestamp,
      showModal,
      initialized,
    });

    // Don't check if not initialized yet or already showing modal
    if (!initialized || showModal) {
      console.debug("[ForceUpdate] Skipping: not initialized or already showing modal");
      return;
    }

    // If initial timestamp is null (app loaded before any force update was set)
    // and we now see a force update, show the modal
    if (initialTimestamp === null) {
      if (currentTimestamp !== null) {
        console.debug("[ForceUpdate] First force update detected, showing modal");
        set({ initialTimestamp: currentTimestamp, showModal: true });
      } else {
        console.debug("[ForceUpdate] Skipping: both timestamps are null");
      }
      return;
    }

    // If current timestamp is null but initial wasn't, don't trigger
    // (this means the admin cleared the force update)
    if (currentTimestamp === null) {
      console.debug("[ForceUpdate] Skipping: currentTimestamp is null");
      return;
    }

    // If current timestamp is greater than initial, show the update modal
    if (currentTimestamp > initialTimestamp) {
      console.debug("[ForceUpdate] Newer timestamp detected, showing modal");
      set({ showModal: true });
    } else {
      console.debug("[ForceUpdate] No newer update available");
    }
  },

  showUpdateModal: () => {
    set({ showModal: true });
  },

  hideUpdateModal: () => {
    set({ showModal: false });
  },
}));
